import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { Checkbox, Clickable } from '@moda/om';
import { PageFilterFacetFragment } from '../../generated/types/PageFilterFacetFragment';
import { PageFilterNameSlugFacetFragment } from '../../generated/types/PageFilterNameSlugFacetFragment';
import { PageFilterSizeFacetFragment } from '../../generated/types/PageFilterSizeFacetFragment';
import { titleize } from '../../lib/titleize';
import { usePLPParams, PLPParamKey } from '../../hooks/usePLPParams';
import './PageFilterFacet.scss';

export const PAGE_FILTER_FACET_FRAGMENT = gql`
  fragment PageFilterFacetFragment on Facet {
    name
    totalItems: total_items
  }
`;

export const PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT = gql`
  fragment PageFilterNameSlugFacetFragment on NameSlugFacet {
    name
    filterValue: filter_value
    slug
    totalItems: total_items
  }
`;

export const PAGE_FILTER_SIZE_FACET_FRAGMENT = gql`
  fragment PageFilterSizeFacetFragment on SizeFacet {
    category
    filterValue: filter_value
    size
    totalItems: total_items
  }
`;

export type Facet = {
  category?: PageFilterSizeFacetFragment['category'];
  filterValue?: PageFilterNameSlugFacetFragment['filterValue'];
  name?: PageFilterNameSlugFacetFragment['filterValue'];
  size?: PageFilterSizeFacetFragment['size'];
  slug?: PageFilterNameSlugFacetFragment['slug'];
  totalItems: PageFilterFacetFragment['totalItems'];
};

export type FacetControlType = 'button' | 'checkbox';

type PageFilterFacetProps = {
  facet: Facet;
  name: PLPParamKey;
  type?: FacetControlType;
  showCounts?: boolean;
};

const getKey = (facet: Facet) => {
  if (facet.slug) {
    return 'slug';
  }

  if (facet.filterValue) {
    return 'filterValue';
  }

  return 'name';
};

export const PageFilterFacet: React.FC<PageFilterFacetProps> = ({
  facet,
  name,
  type = 'checkbox',
  showCounts = true
}) => {
  const { params, addFilter, removeFilter } = usePLPParams();

  const values = useMemo(() => (params[name] as string[]) || [], [params, name]);
  const key = getKey(facet);

  const selected = useMemo(() => values.some(value => value === facet[key]), [facet, key, values]);

  const handleChange = useCallback(() => {
    const value = facet[key];
    if (!value) return;

    selected ? removeFilter(name, value) : addFilter(name, value);
  }, [selected, facet, key, name, addFilter, removeFilter]);

  if (!facet) {
    return null;
  }

  const display = titleize(facet?.name) || facet?.size || '';

  if (type === 'button') {
    return (
      <Clickable
        className={classNames('PageFilterFacet__button', {
          'PageFilterFacet__button--selected': selected
        })}
        onClick={handleChange}
      >
        {display}
      </Clickable>
    );
  }

  if (type === 'checkbox') {
    return (
      <Checkbox
        checked={selected}
        className="PageFilterFacet"
        onChange={handleChange}
        value={facet.filterValue || undefined}
      >
        <span className="PageFilterFacet__info">
          <span>{display}</span>
          {showCounts && <span>{facet.totalItems}</span>}
        </span>
      </Checkbox>
    );
  }

  return null;
};
